import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Camera, Upload, X, MapPin, CalendarDays,
  CheckCircle2, ArrowRight, ArrowLeft, Waves,
} from "lucide-react";
import config from "../../config";
import { isUserLoggedIn } from "../common/Header";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const DAYS_AHEAD    = 3;

const getLoggedInUser = () => {
  try { return JSON.parse(localStorage.getItem("currentUser") || "null"); }
  catch { return null; }
};

const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

const fetchSlotAvailability = async (serviceId, duration) => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ serviceId: String(serviceId), duration, daysAhead: DAYS_AHEAD }),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
  return Array.isArray(data?.days) ? data.days : [];
};

const isSlotSelectable = (slot) =>
  Boolean(slot?.availability) && Number(slot?.seats || 0) - Number(slot?.booked || 0) > 0;

const getSlotState = (slot) => {
  const seats  = Number(slot?.seats || 0);
  const booked = Number(slot?.booked || 0);
  if (booked >= seats) return "booked";
  if (!slot?.availability) return "unavailable";
  return "available";
};

const formatDateLabel = (date) => {
  const [dd, mm, yyyy] = String(date || "").split("-");
  if (!dd) return date;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) { resolve(true); return; }
  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.onload  = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const STEPS = [
  { key: "details",  label: "Details",     sub: "Problem & address"   },
  { key: "schedule", label: "Date & Time", sub: "Pick a slot"         },
  { key: "confirm",  label: "Confirm & Pay", sub: "Consultation charge" },
];

const ConsultationBookingPage = () => {
  const navigate = useNavigate();
  const { bookingId: routeBookingId } = useParams();
  const cameraInputRef = useRef(null);
  const photoInputRef  = useRef(null);

  const loggedInUser = useMemo(() => getLoggedInUser(), []);
  const userMobile   = loggedInUser?.mobile || loggedInUser?.phone || "";
  const userName     = loggedInUser?.name || loggedInUser?.full_name || "";
  const addressKey   = loggedInUser?.id || loggedInUser?.user_id || userMobile;

  // ── View mode: creating a new booking vs viewing a confirmed one ────────
  const [loadingExisting, setLoadingExisting] = useState(!!routeBookingId);
  const [existingBooking, setExistingBooking]  = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!routeBookingId) return;
    setLoadingExisting(true);
    fetch(`${API_BASE_URL}/api/user/consultation/${encodeURIComponent(routeBookingId)}/?mobile=${encodeURIComponent(userMobile)}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data?.success) throw new Error(data?.message || "Booking not found");
        setExistingBooking(data.data);
      })
      .catch((e) => setLoadError(e.message || "Could not load this booking."))
      .finally(() => setLoadingExisting(false));
  }, [routeBookingId, userMobile]);

  // ── Step 1: Details ───────────────────────────────────────────────────
  const [step, setStep] = useState("details");
  const [problem, setProblem]         = useState("");
  const [description, setDescription] = useState("");
  const [photoDataUrl, setPhotoDataUrl] = useState("");

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addrLoading, setAddrLoading]       = useState(false);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const selectedAddress = useMemo(
    () => savedAddresses.find((a) => a.id === selectedAddrId) || null,
    [savedAddresses, selectedAddrId]
  );

  useEffect(() => {
    if (!addressKey || routeBookingId) return;
    setAddrLoading(true);
    fetch(`${API_BASE_URL}/api/user/user-addresses/${encodeURIComponent(addressKey)}/`, {
      credentials: "include", headers: authHeaders(),
    })
      .then((r) => r.json())
      .then((data) => {
        const addrs = Array.isArray(data?.data) ? data.data : [];
        setSavedAddresses(addrs);
        if (addrs.length) setSelectedAddrId(addrs[0].id);
      })
      .catch(() => {})
      .finally(() => setAddrLoading(false));
  }, [addressKey, routeBookingId]);

  const handlePhotoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(String(reader.result || ""));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const detailsValid = problem.trim().length > 0 && !!selectedAddress;

  // ── Step 2: Date & Time (schedule-only — no Instant option) ──────────
  const [days, setDays]                     = useState([]);
  const [loadingAvailability, setLoading]   = useState(false);
  const [availabilityError, setAvailError]  = useState("");
  const [selectedDate, setSelectedDate]     = useState("");
  const [selectedTime, setSelectedTime]     = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");

  const stepIndex = STEPS.findIndex((s) => s.key === step);

  const goToSchedule = async () => {
    setStep("schedule");
    setSelectedDate(""); setSelectedTime(""); setSelectedSlotId("");
    setDays([]); setAvailError(""); setLoading(true);
    try {
      const fetched = await fetchSlotAvailability("technical", 1);
      setDays(fetched);
      const first = fetched.find((d) => d.slots.some(isSlotSelectable))?.date || fetched[0]?.date || "";
      setSelectedDate(first);
    } catch (e) {
      setAvailError(e?.message || "Failed to load available slots.");
    } finally {
      setLoading(false);
    }
  };

  const pickDate = (date) => { setSelectedDate(date); setSelectedTime(""); setSelectedSlotId(""); };
  const pickTime = (time, slotId) => { setSelectedTime(time); setSelectedSlotId(slotId || ""); };
  const activeDay = days.find((d) => d.date === selectedDate) || null;

  const scheduleValid = !!selectedDate && !!selectedTime;

  // ── Step 3: Review pricing + create booking + pay ────────────────────
  const consultationFees = config.CONSULTATION_FEES || { default: 90 };
  const previewCharge = Number(consultationFees["switch-socket"] ?? consultationFees.default ?? 90);
  const gstPercent     = Number(config.CHECKOUT_CHARGES?.GST_PERCENT ?? 18);
  const platformFee    = Number(config.CHECKOUT_CHARGES?.PLATFORM_FEE ?? 20);
  const previewGst     = Math.round(previewCharge * gstPercent / 100 * 100) / 100;
  const previewTotal   = Math.round((previewCharge + previewGst + platformFee) * 100) / 100;

  const [placing, setPlacing] = useState(false);
  const [placeError, setPlaceError] = useState("");
  const [paying, setPaying] = useState(false);

  const goBack = () => {
    if (step === "confirm") { setStep("schedule"); return; }
    if (step === "schedule") { setStep("details"); return; }
    navigate("/services/technical-services");
  };

  const verifyPayment = async (body) => {
    const res  = await fetch(`${API_BASE_URL}/api/payment/verify/`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.message || "Payment verification failed.");
    return data;
  };

  const confirmBookingPayment = async (bookingId, verifyData) => {
    const res  = await fetch(`${API_BASE_URL}/api/user/consultation/${encodeURIComponent(bookingId)}/confirm-payment/`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        mobile: userMobile,
        razorpay_order_id:       verifyData.razorpay_order_id || "",
        razorpay_payment_id:     verifyData.payment_id || "",
        razorpay_transaction_id: verifyData.transaction_id || "",
      }),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.message || "Could not confirm the booking.");
    return data;
  };

  const handleConfirmAndPay = async () => {
    if (!userMobile) {
      navigate("/user/user-login", { state: { redirectTo: "/services/technical-services/consultation" } });
      return;
    }
    if (placing || paying) return;
    setPlacing(true);
    setPlaceError("");
    try {
      // 1) Create the PENDING_PAYMENT booking — server computes the real charge.
      const bookRes = await fetch(`${API_BASE_URL}/api/user/consultation/book/`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          mobile: userMobile,
          customer_name: userName,
          service_id: "switch-socket",
          service_name: "Electrical Consultation",
          problem, description,
          photos: photoDataUrl ? [photoDataUrl] : [],
          address: selectedAddress,
          booking_mode: "schedule",
          booking_date: selectedDate,
          booking_time: selectedTime,
          booking_slot_id: selectedSlotId,
        }),
      });
      const bookData = await bookRes.json();
      if (!bookRes.ok || !bookData?.success) throw new Error(bookData?.message || "Could not create the booking.");
      const booking = bookData.data;
      setPlacing(false);
      setPaying(true);

      // 2) Create the Razorpay order for the real, server-computed total.
      const orderRes = await fetch(`${API_BASE_URL}/api/payment/create-order/`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ amount: booking.consultation_total, mobile: userMobile }),
      });
      const paymentOrder = await orderRes.json();
      if (!orderRes.ok || !paymentOrder?.success) throw new Error(paymentOrder?.message || "Could not initiate payment.");

      const finishPayment = async (verifyBody) => {
        const verifyData = await verifyPayment(verifyBody);
        await confirmBookingPayment(booking.booking_id, verifyData);
        navigate(`/services/technical-services/consultation/${booking.booking_id}`, { replace: true });
      };

      if (paymentOrder.is_test) {
        await finishPayment({ razorpay_order_id: paymentOrder.order_id, razorpay_payment_id: `pay_TEST_${Date.now()}` });
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Razorpay failed to load. Please check your connection and try again.");

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: paymentOrder.key_id, amount: paymentOrder.amount, currency: paymentOrder.currency,
          order_id: paymentOrder.order_id, name: "ClickNido", description: "Technical Consultation",
          handler: async (response) => {
            try {
              await finishPayment({
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              });
              resolve();
            } catch (err) { reject(err); }
          },
          modal: { ondismiss: () => reject(new Error("Payment cancelled.")) },
          theme: { color: "#16a34a" },
        });
        rzp.open();
      });
    } catch (e) {
      setPlaceError(e.message || "Something went wrong. Please try again.");
    } finally {
      setPlacing(false);
      setPaying(false);
    }
  };

  // ── Render: viewing an existing/confirmed booking ────────────────────
  if (routeBookingId) {
    if (loadingExisting) {
      return <div style={S.page}><div style={S.container}><div style={S.stateBox}>Loading your booking…</div></div></div>;
    }
    if (loadError || !existingBooking) {
      return (
        <div style={S.page}><div style={S.container}>
          <div style={S.errorBox}>{loadError || "Booking not found."}</div>
          <Link to="/services/technical-services" style={S.backBtn}><ArrowLeft size={15} /> Back to Technical Services</Link>
        </div></div>
      );
    }
    const b = existingBooking;
    return (
      <div style={S.page}>
        <div style={S.container}>
          <div style={S.breadcrumb}>
            <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
            <Link to="/services/technical-services" style={S.breadcrumbLink}>Technical Services</Link> <span>&gt;</span>{" "}
            <span>Consultation</span>
          </div>
          <div style={S.confirmCard}>
            <div style={S.confirmIconWrap}><CheckCircle2 size={28} color="#16a34a" /></div>
            <h2 style={S.confirmTitle}>Booking Confirmed</h2>
            <div style={S.confirmBookingId}>Booking ID: {b.booking_id}</div>
            <div style={S.confirmStatusChip}>{(b.status || "").replaceAll("_", " ")}</div>
            <div style={S.confirmDetailBox}>
              <div style={S.confirmDetailRow}><span>Problem</span><span>{b.problem}</span></div>
              {b.booking_mode === "schedule" ? (
                <div style={S.confirmDetailRow}><span>Scheduled</span><span>{b.booking_date} · {b.booking_time}</span></div>
              ) : (
                <div style={S.confirmDetailRow}><span>Mode</span><span>Instant</span></div>
              )}
              <div style={S.confirmDetailRow}><span>Consultation Charge</span><span>₹{Number(b.consultation_charge || 0).toFixed(2)}</span></div>
              <div style={S.confirmDetailRow}><span>GST</span><span>₹{Number(b.consultation_gst || 0).toFixed(2)}</span></div>
              <div style={S.confirmDetailRow}><span>Platform Fee</span><span>₹{Number(b.consultation_platform_fee || 0).toFixed(2)}</span></div>
              <div style={S.confirmDetailGrandRow}><span>Paid</span><span>₹{Number(b.consultation_total || 0).toFixed(2)}</span></div>
            </div>
            <p style={S.confirmNote}>
              A technician will be assigned shortly. You'll be able to track inspection, quotation and approval
              from your bookings — the technician visits, inspects the issue and sends you a quotation before any
              further chargeable work is done.
            </p>
            <Link to="/profile-dashboard" style={S.continueBtnLink}>Go to My Bookings <ArrowRight size={16} /></Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: new-booking creation flow ────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
          <Link to="/services/technical-services" style={S.breadcrumbLink}>Technical Services</Link> <span>&gt;</span>{" "}
          <span>Consultation</span>
        </div>

        <div style={S.hero}>
          <div style={S.heroBadge}><Waves size={13} /> ELECTRICAL CONSULTATION</div>
          <h1 style={S.heroTitle}>Not Sure What's Wrong? Book a Consultation</h1>
          <p style={S.heroSub}>A qualified electrician inspects your issue, identifies the problem and sends you a quotation before any chargeable work begins.</p>
        </div>

        <div style={S.stepRow}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={S.stepItem}>
                <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                  {i < stepIndex ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                  <div style={S.stepSub}>{s.sub}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
            </React.Fragment>
          ))}
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {placeError && <div style={S.errorBox}>{placeError}</div>}

        {/* ── STEP 1: Consultation Details ──────────────────────────────── */}
        {step === "details" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>What's the problem?</h2>
            <p style={S.sectionSub}>Tell us what's going on — the electrician inspects and advises after arrival</p>
            <input
              type="text"
              value={problem}
              onChange={(e) => setProblem(e.target.value)}
              placeholder="E.g. Switch not working, sparking socket, need a new point…"
              style={S.textInput}
            />

            <div style={S.noteLabel}>Describe it (optional)</div>
            <textarea
              style={S.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Any extra detail that will help the electrician come prepared."
              rows={3}
            />

            <div style={S.noteLabel}>📷 Add Photo (optional)</div>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={S.hiddenInput} onChange={handlePhotoFile} />
            <input ref={photoInputRef} type="file" accept="image/*" style={S.hiddenInput} onChange={handlePhotoFile} />
            {photoDataUrl ? (
              <div style={S.photoPreviewRow}>
                <img src={photoDataUrl} alt="Attached" style={S.photoPreview} />
                <button type="button" style={S.removeBtn} onClick={() => setPhotoDataUrl("")}><X size={13} /></button>
              </div>
            ) : (
              <div style={S.photoBtnRow}>
                <button type="button" style={S.photoBtn} onClick={() => cameraInputRef.current?.click()}><Camera size={16} /> Take Photo</button>
                <button type="button" style={S.photoBtn} onClick={() => photoInputRef.current?.click()}><Upload size={16} /> Upload Photo</button>
              </div>
            )}

            <div style={{ ...S.noteLabel, marginTop: 22 }}><MapPin size={14} style={{ verticalAlign: "-2px" }} /> Service Address</div>
            {addrLoading ? (
              <div style={S.stateBox}>Loading addresses…</div>
            ) : savedAddresses.length === 0 ? (
              <div style={S.addrEmptyBox}>
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>No saved addresses yet.</div>
                <button type="button" style={S.outlineBtn} onClick={() => navigate("/profile-dashboard", { state: { openAddresses: true } })}>
                  + Add Address in Profile
                </button>
              </div>
            ) : (
              <div style={S.addrList}>
                {savedAddresses.map((addr) => {
                  const active = selectedAddrId === addr.id;
                  const fullAddr = [addr.line1, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ");
                  return (
                    <button
                      type="button" key={addr.id}
                      style={{ ...S.addrCard, ...(active ? S.addrCardActive : {}) }}
                      onClick={() => setSelectedAddrId(addr.id)}
                    >
                      {active ? <CheckCircle2 size={16} color="#16a34a" /> : <div style={S.addrRadio} />}
                      <div>
                        <div style={S.addrLabelText}>{addr.label || "Address"}</div>
                        <div style={S.addrFullText}>{fullAddr}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              style={{ ...S.continueBtn, ...(!detailsValid ? S.continueBtnDisabled : {}) }}
              disabled={!detailsValid}
              onClick={goToSchedule}
            >
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── STEP 2: Date & Time ───────────────────────────────────────── */}
        {step === "schedule" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Choose Date &amp; Time</h2>
            {loadingAvailability ? (
              <div style={S.stateBox}>Loading…</div>
            ) : availabilityError ? (
              <div style={S.errorBox}>{availabilityError}</div>
            ) : (
              <div style={S.scheduleColumns}>
                <div style={S.scheduleCol}>
                  <div style={S.scheduleColTitle}>1. Select Date</div>
                  <div style={S.dateList}>
                    {days.map((day) => {
                      const hasOpen  = day.slots.some(isSlotSelectable);
                      const selected = selectedDate === day.date;
                      return (
                        <button
                          key={day.date} type="button" disabled={!hasOpen}
                          style={{ ...S.dateRow, ...(selected ? S.dateRowActive : {}), ...(!hasOpen ? S.dateRowDisabled : {}) }}
                          onClick={() => hasOpen && pickDate(day.date)}
                        >
                          <CalendarDays size={16} color={selected ? "#fff" : hasOpen ? "#16a34a" : "#9ca3af"} />
                          <span style={S.dateRowLabel}>{formatDateLabel(day.date)}</span>
                          <span style={{ ...S.dateRowSub, ...(selected ? { color: "#bbf7d0" } : {}) }}>
                            {hasOpen ? `${day.slots.filter(isSlotSelectable).length} open` : "Full"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div style={S.scheduleCol}>
                  <div style={S.scheduleColTitle}>2. Select Time Slot</div>
                  <div style={S.slotGrid}>
                    {(activeDay?.slots || []).map((slot) => {
                      const state      = getSlotState(slot);
                      const selectable = state === "available";
                      const freeSeats  = Number(slot.seats || 0) - Number(slot.booked || 0);
                      const selected   = selectedTime === slot.time;
                      return (
                        <button
                          key={`${activeDay?.date}-${slot.time}`} type="button" disabled={!selectable}
                          style={{
                            ...S.slotBtn,
                            ...(selected ? S.slotBtnActive : {}),
                            ...(state === "booked" ? S.slotBtnBooked : {}),
                            ...(state === "unavailable" ? S.slotBtnDisabled : {}),
                          }}
                          onClick={() => selectable && pickTime(slot.time, slot._id)}
                        >
                          <div style={S.slotTimeRow}>
                            <span style={{ ...S.slotTime, ...(selected ? { color: "#fff" } : {}) }}>{slot.time}</span>
                            {selected && <CheckCircle2 size={13} color="#fff" />}
                          </div>
                          <div style={{ ...S.slotMeta, ...(selected ? { color: "#bbf7d0" } : {}), ...(state === "booked" ? { color: "#dc2626", fontWeight: 700 } : {}) }}>
                            {state === "available" ? `${freeSeats} left` : state === "booked" ? "Booked" : "Unavailable"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <button
              type="button"
              style={{ ...S.continueBtn, ...(!scheduleValid ? S.continueBtnDisabled : {}) }}
              disabled={!scheduleValid}
              onClick={() => setStep("confirm")}
            >
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── STEP 3: Confirm & Pay ─────────────────────────────────────── */}
        {step === "confirm" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Review &amp; Pay</h2>
            <p style={S.sectionSub}>You'll be charged only the consultation/visitation fee now — any repair or product cost is quoted separately after inspection.</p>

            <div style={S.reviewBox}>
              <div style={S.reviewRow}><span>Problem</span><span>{problem}</span></div>
              <div style={S.reviewRow}>
                <span>When</span>
                <span>{formatDateLabel(selectedDate)} · {selectedTime}</span>
              </div>
              <div style={S.reviewRow}><span>Address</span><span>{[selectedAddress?.line1, selectedAddress?.city].filter(Boolean).join(", ")}</span></div>
            </div>

            <div style={S.breakdownBox}>
              <div style={S.breakdownRow}><span>Consultation / Visitation Charge</span><span>₹{previewCharge.toFixed(2)}</span></div>
              <div style={S.breakdownRow}><span>GST ({gstPercent}%)</span><span>₹{previewGst.toFixed(2)}</span></div>
              <div style={S.breakdownRow}><span>Platform Fee</span><span>₹{platformFee.toFixed(2)}</span></div>
              <div style={S.breakdownGrandRow}><span>Total Payable Now</span><span>₹{previewTotal.toFixed(2)}</span></div>
              <div style={S.breakdownHint}>Final price may be confirmed server-side at checkout.</div>
            </div>

            <button
              type="button"
              style={{ ...S.continueBtn, ...((placing || paying) ? S.continueBtnDisabled : {}) }}
              disabled={placing || paying}
              onClick={handleConfirmAndPay}
            >
              {placing ? "Creating booking…" : paying ? "Processing payment…" : isUserLoggedIn() ? "Confirm & Pay" : "Login & Continue"} <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "900px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px", flexWrap: "wrap" },
  breadcrumbLink: { color: "#16a34a", fontWeight: 600, textDecoration: "none" },

  hero:      { textAlign: "center", maxWidth: "640px", margin: "0 auto 26px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#dcfce7", color: "#16a34a", fontSize: "11px", fontWeight: 800, padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: 0, fontSize: "24px", fontWeight: 800, letterSpacing: "-0.5px", color: "#111827" },
  heroSub:   { margin: "8px 0 0", fontSize: "13.5px", color: "#6b7280", lineHeight: 1.5 },

  stepRow: { display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "4px", margin: "0 0 28px", flexWrap: "wrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "8px" },
  stepCircle: { width: "30px", height: "30px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#16a34a", color: "#fff" },
  stepCircleDone: { background: "#bbf7d0", color: "#16a34a" },
  stepLabel: { fontSize: "13px", fontWeight: 700, color: "#9ca3af", lineHeight: 1.3 },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "11px", color: "#9ca3af", marginTop: "1px" },
  stepConnector: { width: "34px", height: "2px", background: "#e5e7eb", marginTop: "15px", flexShrink: 0 },
  stepConnectorActive: { background: "#16a34a" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0, textDecoration: "none" },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "22px", marginBottom: "24px" },
  sectionTitle: { margin: "0 0 4px", fontSize: "19px", fontWeight: 800, color: "#111827" },
  sectionSub: { margin: "0 0 16px", fontSize: "13px", color: "#6b7280" },
  noteLabel: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "8px" },

  textInput: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", marginBottom: "16px", boxSizing: "border-box", fontFamily: "inherit" },
  textarea:  { width: "100%", minHeight: "80px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontFamily: "inherit", fontSize: "13px", resize: "vertical", marginBottom: "16px", boxSizing: "border-box" },

  hiddenInput: { display: "none" },
  photoBtnRow: { display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" },
  photoBtn: { display: "inline-flex", alignItems: "center", gap: "8px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "11px 18px", borderRadius: "10px" },
  photoPreviewRow: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" },
  photoPreview: { width: "80px", height: "80px", objectFit: "cover", borderRadius: "12px", border: "1px solid #e5e7eb" },
  removeBtn: { width: "22px", height: "22px", borderRadius: "50%", border: "none", background: "#fee2e2", color: "#b91c1c", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },

  addrEmptyBox: { background: "#f8fafc", border: "1px dashed #e5e7eb", borderRadius: "12px", padding: "16px", textAlign: "center", marginBottom: "16px" },
  outlineBtn: { background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", padding: "9px 16px", borderRadius: "10px", cursor: "pointer" },
  addrList: { display: "flex", flexDirection: "column", gap: "8px", marginBottom: "18px" },
  addrCard: { display: "flex", alignItems: "flex-start", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  addrCardActive: { borderColor: "#16a34a", boxShadow: "0 0 0 3px rgba(22,163,74,0.12)" },
  addrRadio: { width: "16px", height: "16px", borderRadius: "50%", border: "1.5px solid #d1d5db", flexShrink: 0, marginTop: "2px" },
  addrLabelText: { fontSize: "13px", fontWeight: 800, color: "#111827" },
  addrFullText: { fontSize: "12.5px", color: "#6b7280", marginTop: "2px" },


  scheduleColumns: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px", marginBottom: "22px", alignItems: "start" },
  scheduleCol: { background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px" },
  scheduleColTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "14px" },
  dateList: { display: "flex", flexDirection: "column", gap: "8px" },
  dateRow: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  dateRowActive: { background: "#16a34a", borderColor: "#16a34a", color: "#fff" },
  dateRowDisabled: { opacity: 0.45, cursor: "not-allowed" },
  dateRowLabel: { flex: 1, fontSize: "13px", fontWeight: 700, color: "inherit" },
  dateRowSub: { fontSize: "11px", fontWeight: 700, color: "#16a34a" },
  slotGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "8px" },
  slotBtn:  { padding: "9px 6px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "center" },
  slotBtnActive: { border: "1.5px solid #16a34a", background: "#16a34a" },
  slotBtnBooked: { border: "1.5px solid #fecaca", background: "#fef2f2", cursor: "not-allowed" },
  slotBtnDisabled: { opacity: 0.5, cursor: "not-allowed", background: "#f3f4f6" },
  slotTimeRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" },
  slotTime: { fontSize: "12px", fontWeight: 800, color: "#111827" },
  slotMeta: { fontSize: "10px", color: "#9ca3af", marginTop: "3px" },

  reviewBox: { background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "14px 16px", marginBottom: "16px" },
  reviewRow: { display: "flex", justifyContent: "space-between", gap: "12px", fontSize: "13px", color: "#374151", padding: "5px 0" },

  breakdownBox: { background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "14px", padding: "14px 16px", marginBottom: "16px" },
  breakdownRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", marginBottom: "6px" },
  breakdownGrandRow: { display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 800, color: "#111827", paddingTop: "6px", borderTop: "1px solid #eceef5" },
  breakdownHint: { fontSize: "11px", color: "#9ca3af", marginTop: "8px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#16a34a", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  continueBtnLink: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#16a34a", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", textDecoration: "none", boxSizing: "border-box" },

  stateBox: { padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "13px" },
  errorBox: { padding: "14px", borderRadius: "10px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: "13px", marginBottom: "16px" },

  confirmCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "32px 24px", textAlign: "center" },
  confirmIconWrap: { width: "56px", height: "56px", borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" },
  confirmTitle: { margin: "0 0 6px", fontSize: "20px", fontWeight: 800 },
  confirmBookingId: { fontSize: "13px", color: "#6b7280", marginBottom: "10px" },
  confirmStatusChip: { display: "inline-block", background: "#ecfdf3", color: "#15803d", border: "1px solid #bbf7d0", fontSize: "11px", fontWeight: 800, padding: "4px 12px", borderRadius: "999px", marginBottom: "20px", textTransform: "capitalize" },
  confirmDetailBox: { background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "14px 16px", textAlign: "left", marginBottom: "18px" },
  confirmDetailRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", padding: "5px 0" },
  confirmDetailGrandRow: { display: "flex", justifyContent: "space-between", fontSize: "15px", fontWeight: 800, color: "#111827", paddingTop: "8px", marginTop: "4px", borderTop: "1px solid #eceef5" },
  confirmNote: { fontSize: "12.5px", color: "#6b7280", lineHeight: 1.5, marginBottom: "20px" },
};

export default ConsultationBookingPage;
